/**
 * Computed label guides (safe area, trim/die-cut line, matrix code zone) and the
 * master guide style — shared by the web label editor and the admin label
 * verification viewer so both render identical guides.
 *
 * Pure JSON→JSON + SVG string building, no browser/Polotno deps (same contract
 * as polotno-rst-guides). The produced elements are sparse fragments; callers
 * that need a fully canonical Design run them through `parseDesign`
 * (@polotno/schema) app-side, or rely on Polotno's own loadJSON normalization.
 */

import { isGuideElement } from "./polotno-canonical"
import { DEFAULT_OUTPUT_DPI, MATRIX_CODE_MM } from "./polotno-constants"
import type { LabelGuideStyleConfig, RstGuideStyle } from "./polotno-rst-guides"

// Guides carry only the props that matter; schema normalization app-side fills
// the remaining defaults. Structural types — intentionally not @polotno/schema.
export type GuideFigureElement = {
  id: string
  type: "figure"
  subType: "rect"
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
  stroke?: string
  strokeWidth?: number
  dash?: number[]
  cornerRadius?: number
  selectable?: boolean
  showInExport?: boolean
  alwaysOnTop?: boolean
  name?: string
}

export type GuideSvgElement = {
  id: string
  type: "svg"
  src: string
  x?: number
  y?: number
  width?: number
  height?: number
  selectable?: boolean
  showInExport?: boolean
  alwaysOnTop?: boolean
  name?: string
}

export type GuideElementInput = GuideFigureElement | GuideSvgElement

/**
 * Master style controls for every editor guide — the single source of truth for
 * how guides look (colors, line thickness, dash, tints), used by BOTH the AAV
 * computed guides and the RST embedded-shape guides.
 *
 * `strokeWidth` / `dash` are raw canvas px, applied as-is (no size scaling) —
 * per-label overrides come from Sanity via {@link resolveGuideStyle}. Geometry
 * (positions, corner radii, insets) is driven by the product's physical spec and
 * lives in the element builders / injection, not here.
 */
export const GUIDE_STYLE = {
  // Safe area: a solid backing line under a dashed line, for contrast on any bg.
  safeArea: {
    dashColor: "#2AB672", // dashed line on top
    backingColor: "#FFFFFF", // solid line underneath
    strokeWidth: 4,
    dash: [10, 12] as number[], // [dashLength, gapLength]
  },
  // Trim line: the die-cut boundary — a single solid line.
  trimLine: {
    color: "#000000",
    strokeWidth: 4,
  },
  // Fold lines (RST die-cut): a distinct dashed line + solid backing, like the
  // safe area but its own color.
  foldLine: {
    dashColor: "#000000",
    backingColor: "#FFFFFF",
    strokeWidth: 1,
    dash: [10, 9] as number[],
  },
  // Text placement guides (RST): a subtle neutral wash hinting at copy zones.
  textGuide: {
    fill: "#98A2B3",
    opacity: 0.22,
  },
  // Bleed: the workspace band (square labels) and the rounded trim mask both use
  // this tint, so they always match.
  bleed: {
    tint: "#2AB672",
    opacity: 0.3,
  },
  // Matrix code reserved zone.
  matrixZone: {
    fill: "#ED8936",
    fillOpacity: 0.15,
    stroke: "#ED8936",
    strokeWidth: 2,
  },
} as const

/** Use a Sanity override only when it's a usable value, else the default. */
const pickStr = (v: string | undefined | null, fallback: string): string =>
  typeof v === "string" && v.length > 0 ? v : fallback
const pickNum = (v: number | undefined | null, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback

/**
 * Resolve the effective guide style: a per-label-size Sanity override (colors,
 * dashes, stroke widths, opacity — the labelSize `guideStyle`) merged
 * field-by-field over the built-in GUIDE_STYLE defaults. Returns the fully
 * resolved `RstGuideStyle`, used by BOTH the AAV computed guides
 * ({@link createGuideElements}) and the RST embedded-shape guides
 * (`injectRstStyledGuides`), plus the legend and workspace tints. `null`/absent
 * (or any unset field) falls back to the default, so the look never regresses.
 * Line widths / dashes are raw canvas px, applied as-is (no size scaling).
 */
export function resolveGuideStyle(
  sanity?: LabelGuideStyleConfig | null,
): RstGuideStyle {
  const s = sanity ?? {}
  const sa = s.safeArea ?? {}
  const tl = s.trimLine ?? {}
  const fl = s.foldLine ?? {}
  const tg = s.textGuide ?? {}
  const bl = s.bleed ?? {}
  const mz = s.matrixZone ?? {}
  return {
    safeArea: {
      dashColor: pickStr(sa.dashColor, GUIDE_STYLE.safeArea.dashColor),
      backingColor: pickStr(sa.backingColor, GUIDE_STYLE.safeArea.backingColor),
      strokeWidth: pickNum(sa.strokeWidth, GUIDE_STYLE.safeArea.strokeWidth),
      dash: [
        pickNum(sa.dashLength, GUIDE_STYLE.safeArea.dash[0] ?? 0),
        pickNum(sa.dashGap, GUIDE_STYLE.safeArea.dash[1] ?? 0),
      ],
    },
    trimLine: {
      color: pickStr(tl.color, GUIDE_STYLE.trimLine.color),
      strokeWidth: pickNum(tl.strokeWidth, GUIDE_STYLE.trimLine.strokeWidth),
    },
    foldLine: {
      dashColor: pickStr(fl.dashColor, GUIDE_STYLE.foldLine.dashColor),
      backingColor: pickStr(fl.backingColor, GUIDE_STYLE.foldLine.backingColor),
      strokeWidth: pickNum(fl.strokeWidth, GUIDE_STYLE.foldLine.strokeWidth),
      dash: [
        pickNum(fl.dashLength, GUIDE_STYLE.foldLine.dash[0] ?? 0),
        pickNum(fl.dashGap, GUIDE_STYLE.foldLine.dash[1] ?? 0),
      ],
    },
    bleed: {
      tint: pickStr(bl.tint, GUIDE_STYLE.bleed.tint),
      opacity: pickNum(bl.opacity, GUIDE_STYLE.bleed.opacity),
    },
    matrixZone: {
      fill: pickStr(mz.fill, GUIDE_STYLE.matrixZone.fill),
      fillOpacity: pickNum(mz.fillOpacity, GUIDE_STYLE.matrixZone.fillOpacity),
      stroke: pickStr(mz.stroke, GUIDE_STYLE.matrixZone.stroke),
      strokeWidth: pickNum(mz.strokeWidth, GUIDE_STYLE.matrixZone.strokeWidth),
    },
    textGuide: {
      fill: pickStr(tg.fill, GUIDE_STYLE.textGuide.fill),
      opacity: pickNum(tg.opacity, GUIDE_STYLE.textGuide.opacity),
    },
  }
}

/** CSS rgba() string from a #rrggbb hex and an opacity in [0, 1]. */
export function hexToRgba(hex: string, opacity: number): string {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/** The bleed tint as an rgba() string — for Polotno's workspace bleedColor and
 * the legend's bleed swatch. Takes the resolved style so a Sanity restyle
 * (per label size) updates it too. */
export function bleedRgba(style: RstGuideStyle): string {
  return hexToRgba(style.bleed.tint, style.bleed.opacity)
}

/**
 * Fill for Polotno's rectangular bleed band (the Workspace `bleedColor` prop).
 *
 * `transparent` — not a collapsed bleed — is how the band is hidden: Polotno only
 * sizes the artboard as trim + bleed while `store.bleedVisible` is on, so turning
 * that off to hide the band would shrink the page to the trim and reflow/re-zoom
 * the whole canvas. Left transparent, the band keeps its size and shows the page
 * background through it (white by default), so the artboard reads as one
 * continuous sheet at a stable size.
 *
 * Two cases draw no band:
 *  - `showBleed` off — the legend's Bleed Area control;
 *  - `isRoundedDieCut` — the trim-mask guide draws those labels' bleed with a
 *    rounded inner edge, so a rectangular band would seam at the corners. Its own
 *    tint follows `showBleed` via the guide's opacity.
 */
export function bleedBandColor({
  style,
  showBleed,
  isRoundedDieCut,
}: {
  style: RstGuideStyle
  showBleed: boolean
  isRoundedDieCut: boolean
}): string {
  if (!showBleed || isRoundedDieCut) return "transparent"
  return bleedRgba(style)
}

/**
 * SVG path for a rounded rectangle at offset (ox, oy) — mirrors Polotno's own
 * figure `rectData` so the mask's inner edge traces exactly the same curve as the
 * trim line. Exported for the tour's bleed pulse, which cuts the same rounded
 * inner edge out of its glow band. */
export function roundedRectPath(
  ox: number,
  oy: number,
  width: number,
  height: number,
  radius: number,
): string {
  const c = Math.min(radius, width / 2, height / 2)
  const x = (v: number) => ox + v
  const y = (v: number) => oy + v
  return `M ${x(0)} ${y(c)} A ${c} ${c} 0 0 1 ${x(c)} ${y(0)} L ${x(width - c)} ${y(0)} A ${c} ${c} 0 0 1 ${x(width)} ${y(c)} L ${x(width)} ${y(height - c)} A ${c} ${c} 0 0 1 ${x(width - c)} ${y(height)} L ${x(c)} ${y(height)} A ${c} ${c} 0 0 1 ${x(0)} ${y(height - c)} Z`
}

/** Base64 data URI for an SVG string, working in both browser and Node/jsdom. */
function svgToDataUri(svg: string): string {
  const g = globalThis as { btoa?: (s: string) => string; Buffer?: unknown }
  const base64 = g.btoa
    ? g.btoa(unescape(encodeURIComponent(svg)))
    : (
        g.Buffer as {
          from: (s: string, e: string) => { toString: (e: string) => string }
        }
      )
        .from(svg, "utf8")
        .toString("base64")
  return `data:image/svg+xml;base64,${base64}`
}

/** Creates guide elements (safe area borders, trim line, matrix zone) for a label canvas */
export function createGuideElements(input: {
  canvasWidth: number
  canvasHeight: number
  safeInsetPx: number
  matrixCodePx: number
  // Fixed matrix-code top-left position in px, from the trim's top-left corner.
  // Defaults to the generic bottom-left-of-safe-area placement when omitted.
  matrixXPx?: number
  matrixYPx?: number
  // Rounded die-cut corner radius in px (0 = square corners). Applies to both the
  // safe-area and trim-line guides so they trace the same rounded boundary.
  cornerRadiusPx?: number
  // Corner radius (px) for the safe area — defaults to cornerRadiusPx.
  safeCornerRadiusPx?: number
  // Bleed extent in px. The rounded trim mask extends this far past the trim
  // edge so it covers the whole bleed area as one continuous rounded shape.
  bleedPx?: number
  safeZoneEnabled: boolean
  matrixCodeEnabled: boolean
  trimLineEnabled?: boolean
  // Resolved guide style (Sanity-over-defaults). Defaults to the built-in style.
  style?: RstGuideStyle
}): GuideElementInput[] {
  const {
    canvasWidth,
    canvasHeight,
    safeInsetPx,
    matrixCodePx,
    matrixXPx,
    matrixYPx,
    cornerRadiusPx = 0,
    safeCornerRadiusPx = input.cornerRadiusPx ?? 0,
    bleedPx = 0,
    safeZoneEnabled,
    matrixCodeEnabled,
    trimLineEnabled,
    style = resolveGuideStyle(),
  } = input

  // Resolved style px, applied as-is (no size scaling).
  const safeStrokeWidth = style.safeArea.strokeWidth
  const safeDash = [...style.safeArea.dash]
  const trimStrokeWidth = style.trimLine.strokeWidth
  const matrixStrokeWidth = style.matrixZone.strokeWidth

  // Safe area guide — a solid white line underneath a green dashed line, so it
  // reads on any background: the green dashes show on light backgrounds while the
  // white backing (visible through the dash gaps) shows on dark ones.
  const safeArea: GuideFigureElement = {
    id: "safe-area",
    type: "figure",
    subType: "rect",
    x: safeInsetPx,
    y: safeInsetPx,
    width: canvasWidth - safeInsetPx * 2,
    height: canvasHeight - safeInsetPx * 2,
    fill: "transparent",
    stroke: style.safeArea.backingColor,
    strokeWidth: safeStrokeWidth,
    dash: [],
    cornerRadius: safeCornerRadiusPx,
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Safe Area",
  }

  const safeAreaAlt: GuideFigureElement = {
    id: "safe-area-alt",
    type: "figure",
    subType: "rect",
    x: safeInsetPx,
    y: safeInsetPx,
    width: canvasWidth - safeInsetPx * 2,
    height: canvasHeight - safeInsetPx * 2,
    fill: "transparent",
    stroke: style.safeArea.dashColor,
    strokeWidth: safeStrokeWidth,
    dash: safeDash,
    cornerRadius: safeCornerRadiusPx,
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Safe Area Alt",
  }

  // Trim mask — the entire bleed area for a rounded die cut, drawn as ONE shape so
  // there's no seam between straight edges and rounded corners. It spans the full
  // canvas including bleed (Polotno's own rectangular band is hidden for rounded
  // labels) with an even-odd cutout at the rounded trim, tinted like the bleed so
  // everything outside the die cut reads as non-printable.
  const cornerRadius = Math.min(
    cornerRadiusPx,
    canvasWidth / 2,
    canvasHeight / 2,
  )
  const maskW = canvasWidth + bleedPx * 2
  const maskH = canvasHeight + bleedPx * 2
  const maskSvg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${maskW}" height="${maskH}" viewBox="0 0 ${maskW} ${maskH}">` +
    `<path fill="${style.bleed.tint}" fill-opacity="${style.bleed.opacity}" fill-rule="evenodd" ` +
    `d="M0 0 H${maskW} V${maskH} H0 Z ${roundedRectPath(bleedPx, bleedPx, canvasWidth, canvasHeight, cornerRadius)}"/></svg>`

  const trimMask: GuideSvgElement = {
    id: "trim-mask",
    type: "svg",
    src: svgToDataUri(maskSvg),
    // Offset into the bleed region so the mask covers the full bleed, not just
    // the trim area. Element coords are relative to the trim's top-left (0,0).
    x: -bleedPx,
    y: -bleedPx,
    width: maskW,
    height: maskH,
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Trim Mask",
  }

  // Trim line — the die-cut boundary at the exact trim edge, a solid black rounded
  // rect matching the physical cut. It sits directly on the mask's rounded cutout
  // edge (no inset) so there's no gap between the line and the bleed. Guide z-order
  // keeps it above the mask.
  const trimLine: GuideFigureElement = {
    id: "trim-line",
    type: "figure",
    subType: "rect",
    x: 0,
    y: 0,
    width: canvasWidth,
    height: canvasHeight,
    fill: "transparent",
    stroke: style.trimLine.color,
    strokeWidth: trimStrokeWidth,
    dash: [],
    cornerRadius: cornerRadiusPx,
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Trim Line",
  }

  // Matrix code zone. Generic products place it bottom-left of the safe area;
  // an explicit position (AAV9) overrides both axes to clear the rounded corner.
  const matrixX = matrixXPx ?? safeInsetPx
  const matrixY = matrixYPx ?? canvasHeight - safeInsetPx - matrixCodePx

  // The data-matrix placeholder rendered UNDER the zone overlay: a code with
  // its white quiet zone, so the reserved square reads as "a code is printed
  // here" even with the orange overlay toggled off — its id is deliberately
  // outside MATRIX_GUIDE_IDS, so the legend's matrix toggle never hides it.
  // Same position/size as the overlay; listed before it so it stays one step
  // below in the guide z-order (lockAndRaiseGuides raises in append order).
  const matrixReserved: GuideSvgElement = {
    id: "matrix-reserved",
    type: "svg",
    src: svgToDataUri(MATRIX_RESERVED_SVG),
    x: matrixX,
    y: matrixY,
    width: matrixCodePx,
    height: matrixCodePx,
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Matrix Code",
  }

  const matrixZone: GuideFigureElement = {
    id: "matrix-zone",
    type: "figure",
    subType: "rect",
    x: matrixX,
    y: matrixY,
    width: matrixCodePx,
    height: matrixCodePx,
    fill: hexToRgba(style.matrixZone.fill, style.matrixZone.fillOpacity),
    stroke: style.matrixZone.stroke,
    strokeWidth: matrixStrokeWidth,
    dash: [],
    selectable: false,
    showInExport: false,
    alwaysOnTop: true,
    name: "Matrix Code Zone",
  }

  // The mask is listed first so it ends up lowest in the guide z-order (above
  // the design, below the trim/safe lines that lockAndRaiseGuides raises after).
  return [
    ...(cornerRadiusPx > 0 ? [trimMask] : []),
    ...(safeZoneEnabled ? [safeArea, safeAreaAlt] : []),
    ...(trimLineEnabled ? [trimLine] : []),
    ...(matrixCodeEnabled ? [matrixReserved, matrixZone] : []),
  ]
}

export type GuideInjectionOptions = {
  safeZoneEnabled: boolean
  matrixCodeEnabled: boolean
  // Die-cut trim-line guide (currently AAV9 SKUs). Defaults to off.
  trimLineEnabled?: boolean
  safeZoneInset: number
  // Rounded die-cut corner radius in inches (0 = square), for the trim line and
  // bleed mask (currently AAV9 SKUs).
  cornerRadiusInches?: number
  // Safe-area corner radius in inches (0 = square). Slightly tighter than the
  // trim radius so the safe area curves just inside the cut. Defaults to
  // `cornerRadiusInches` when not provided.
  safeCornerRadiusInches?: number
  // Fixed matrix-code position in inches (top-left of its bounding box, from the
  // trim's top-left corner) — set for AAV9 SKUs so the code clears the rounded
  // corner. `null`/absent falls back to the generic bottom-left-of-safe-area spot.
  matrixCodePositionInches?: { x: number; y: number } | null
  trimWidthInches?: number
  trimHeightInches?: number
  // Per-label-size guide look (colors/dashes/stroke/opacity) from Sanity, merged
  // over GUIDE_STYLE defaults. Absent → the built-in defaults.
  guideStyle?: LabelGuideStyleConfig | null
}

// Minimal structural shape of a Polotno design as this module reads it (same
// loose-JSON convention as polotno-rst-guides).
type DesignLike = Record<string, unknown> & {
  width: number
  height: number
  pages: Array<
    Record<string, unknown> & {
      bleed?: unknown
      children: Array<{ id: string }>
    }
  >
}

/**
 * Inject computed guide elements (safe area / trim line / matrix zone) into a
 * stored label JSON for display. The result's guide elements are sparse input
 * fragments — callers that need a fully canonical Design normalize app-side
 * (web: `parseDesign`; a plain Polotno `loadJSON` also applies its defaults).
 */
export function injectComputedGuideElements<T>(
  json: T,
  opts: GuideInjectionOptions,
): T {
  const storeJson = json as unknown as DesignLike
  const trimWidthInches =
    typeof opts.trimWidthInches === "number" &&
    Number.isFinite(opts.trimWidthInches) &&
    opts.trimWidthInches > 0
      ? opts.trimWidthInches
      : undefined
  const trimHeightInches =
    typeof opts.trimHeightInches === "number" &&
    Number.isFinite(opts.trimHeightInches) &&
    opts.trimHeightInches > 0
      ? opts.trimHeightInches
      : undefined

  // Keep guides physically consistent by mapping inches to template-native px.
  const templatePxPerInch =
    trimWidthInches && storeJson.width > 0
      ? storeJson.width / trimWidthInches
      : trimHeightInches && storeJson.height > 0
        ? storeJson.height / trimHeightInches
        : DEFAULT_OUTPUT_DPI

  const safeInsetPx = opts.safeZoneInset * templatePxPerInch
  const matrixCodePx = (MATRIX_CODE_MM / 25.4) * templatePxPerInch
  const cornerRadiusPx = (opts.cornerRadiusInches ?? 0) * templatePxPerInch
  const safeCornerRadiusPx =
    (opts.safeCornerRadiusInches ?? opts.cornerRadiusInches ?? 0) *
    templatePxPerInch
  // Bleed extent (px) for the rounded trim mask — taken from the page's own bleed
  // so the mask lines up with Polotno's bleed area.
  const bleedPx = storeJson.pages.reduce(
    (max, page) =>
      typeof page.bleed === "number" && page.bleed > max ? page.bleed : max,
    0,
  )

  // The matrix-code position (`matrixCodePositionInches`) is already trim-origin,
  // top-left of the code's bounding box, in inches — the API resolves it from
  // Sanity's bottom-left `matrixCoordinateX/Y` (or the legacy AAV9 fallback) and
  // does any bleed handling there. So map inches → px directly. Only applied when
  // both coords are finite so a stale/missing value falls back to the generic
  // bottom-left-of-safe-area placement.
  const matrixPos = opts.matrixCodePositionInches
  const hasMatrixPos =
    matrixPos != null &&
    Number.isFinite(matrixPos.x) &&
    Number.isFinite(matrixPos.y)
  const matrixXPx = hasMatrixPos ? matrixPos.x * templatePxPerInch : undefined
  const matrixYPx = hasMatrixPos ? matrixPos.y * templatePxPerInch : undefined

  const guides = createGuideElements({
    canvasWidth: storeJson.width,
    canvasHeight: storeJson.height,
    safeInsetPx,
    matrixCodePx,
    matrixXPx,
    matrixYPx,
    cornerRadiusPx,
    safeCornerRadiusPx,
    bleedPx,
    safeZoneEnabled: opts.safeZoneEnabled,
    matrixCodeEnabled: opts.matrixCodeEnabled,
    trimLineEnabled: opts.trimLineEnabled ?? false,
    style: resolveGuideStyle(opts.guideStyle),
  })
  // Safe-area and trim-line guides live on every page; the matrix guides (the
  // data-matrix placeholder + the zone overlay above it) only on the back page
  // (page 2 when present, else page 1). Order within `guides` is preserved so
  // the placeholder stays below the overlay.
  const matrixGuideIds = new Set(["matrix-reserved", "matrix-zone"])
  const perPageGuides = guides.filter((guide) => !matrixGuideIds.has(guide.id))
  const matrixGuides = guides.filter((guide) => matrixGuideIds.has(guide.id))
  const matrixZonePageIndex = storeJson.pages.length > 1 ? 1 : 0

  return {
    ...storeJson,
    pages: storeJson.pages.map((page, pageIndex) => ({
      ...page,
      children: [
        ...page.children.filter((c) => !isGuideElement(c.id)),
        ...perPageGuides,
        ...(pageIndex === matrixZonePageIndex ? matrixGuides : []),
      ],
    })),
  } as unknown as T
}

// "Reserved For Internal Use" data-matrix placeholder (5.5mm square incl. the
// white quiet zone — the same square the zone overlay marks). Inlined as an
// SVG string → data URI so the guide needs no hosted asset.
const MATRIX_RESERVED_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.59 15.59">' +
  '<rect width="15.59" height="15.59" fill="#fff"/>' +
  '<path d="M.71.71h.71v2.13h1.42v-.71h.71V.71h.71v.71h.71v2.13h-1.42v.71h3.54v.71h-.71v.71h-1.42v-.71h-.71v.71h-.71v-.71h-.71v-.71h-.71v.71h-.71v1.42h.71v.71h-.71v1.42h.71v-.71h.71v.71h.71v-1.42h-.71v-1.42h.71v.71h.71v.71h1.42v.71h.71v.71h-.71v.71h-.71v1.42h-.71v-.71h-.71v.71h-.71v.71h-1.42v.71h.71v.71h-.71v.71h.71v.71h1.42v-1.42h-.71v-1.42h.71v.71h1.42v.71h-.71v.71h.71v.71h2.83v-.71h-.71v-.71h1.42v1.42h2.13v-.71h.71v.71h.71v-2.13h1.42v-.71h-1.42v.71h-1.42v.71h-.71v.71h-.71v-1.42h.71v-.71h-.71v-.71h1.42v.71h.71v-.71h.71v-.71h-.71v-.71h1.42v1.42h.71v-.71h1.42v.71h-.71v.71h.71v.71h-.71v.71h-.71v.71h-.71v.71h.71v-.71h.71v.71h.71v.71H.71V.71ZM2.13.71h.71v.71h-.71v-.71ZM4.96.71h.71v.71h-.71v-.71ZM6.38.71h.71v1.42h-.71V.71ZM7.8.71h.71v.71h-.71v-.71ZM9.21.71h.71v.71h-.71v-.71ZM10.63.71h.71v.71h.71v.71h-2.13v-.71h.71v-.71ZM12.05.71h.71v.71h-.71v-.71ZM13.46.71h.71v.71h.71v.71h-1.42v.71h1.42v.71h-.71v.71h.71v.71h-1.42v-1.42h-.71v.71h-.71v-.71h-.71v1.42h.71v2.13h-1.42v-.71h.71v-.71h-.71v-.71h-1.42v-.71h.71v-.71h.71v-.71h2.13v-1.42h.71v-.71ZM5.67,2.13h.71v.71h-.71v-.71ZM9.21,2.13h.71v1.42h-.71v-1.42ZM7.09,2.83h.71v1.42h-.71v-1.42ZM2.13,4.96h.71v.71h-.71v-.71ZM8.5,4.96h.71v.71h-.71v-.71ZM4.25,5.67h.71v.71h-.71v-.71ZM7.09,5.67h.71v.71h.71v1.42h1.42v-.71h.71v.71h.71v.71h-2.13v.71h-.71v.71h-.71v.71h-.71v-1.42h.71v-.71h-.71v-.71h.71v-.71h-.71v-1.42ZM9.21,5.67h1.42v.71h-.71v.71h-.71v-1.42ZM12.76,5.67h2.13v.71h-2.13v-.71ZM12.05,7.09h.71v1.42h-.71v-1.42ZM14.17,7.09h.71v.71h-.71v-.71ZM13.46,7.8h.71v.71h.71v.71h-1.42v-1.42ZM1.42,9.21v.71h1.42v-.71h-1.42ZM9.92,9.21h.71v.71h-.71v-.71ZM5.67,9.92h.71v.71h-.71v-.71ZM10.63,9.92h.71v.71h-.71v-.71ZM6.38,10.63h.71v.71h.71v-.71h.71v.71h.71v.71h-2.13v.71h-.71v-.71h-1.42v-.71h1.42v-.71ZM2.13,12.76h.71v.71h-.71v-.71ZM4.96,12.76h1.42v.71h-1.42v-.71ZM14.17,12.76h.71v.71h-.71v-.71Z" fill-rule="evenodd"/>' +
  "</svg>"
